import { useEffect, useState } from 'react';
import { Alert, Button, ControlLabel, FormControl, FormGroup, Modal, Row, Col } from 'react-bootstrap';
import { connect, Dispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { IApplicationState, OrdersActionCreators, AppActionCreators } from '@nkr/store/index';
import { ICheekSwabRequest } from '@nkr/models/index';

interface IProps {
    show: boolean;
    donorId: number;
    onCancel: () => void;
    onSuccess: () => void;
}

interface IDispatchProps {
    createDonorOrder: (donorId: number, request: ICheekSwabRequest) => Promise<any>;
    showLoading: (message: string) => Promise<void>;
    hideLoading: () => void;
}

type TProps = IProps & IDispatchProps;

interface IValidation {
    emailIsValid: boolean;
    dobIsValid: boolean;
    firstNameIsValid: boolean;
    lastNameIsValid: boolean;
    address1IsValid: boolean;
    cityIsValid: boolean;
    stateIsValid: boolean;
    postalCodeIsValid: boolean;
    contactPhoneIsValid: boolean;
}

const BulkCheekSwabModal = (props: TProps) => {
    const [formData, setFormData] = useState<ICheekSwabRequest>({
        email: '',
        dob: '',
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        contactPhone: ''
    });
    const [dirty, setDirty] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!props.show) {
            setFormData({
                email: '',
                dob: '',
                firstName: '',
                lastName: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                postalCode: '',
                contactPhone: ''
            });
            setDirty(false);
            setIsSubmitting(false);
        }
    }, [props.show]);

    const validate = (dirty: boolean): IValidation & { isValid: boolean } => {
        const emailIsValid = dirty ? formData.email.length > 0 : true;
        const dobIsValid = dirty ? formData.dob.length > 0 : true;
        const firstNameIsValid = dirty ? formData.firstName.length > 0 : true;
        const lastNameIsValid = dirty ? formData.lastName.length > 0 : true;
        const address1IsValid = dirty ? formData.address1.length > 0 : true;
        const cityIsValid = dirty ? formData.city.length > 0 : true;
        const stateIsValid = dirty ? formData.state.length > 0 : true;
        const postalCodeIsValid = dirty ? formData.postalCode.length > 0 : true;
        const contactPhoneIsValid = dirty ? formData.contactPhone.length > 0 : true;

        const isValid =
            emailIsValid &&
            dobIsValid &&
            firstNameIsValid &&
            lastNameIsValid &&
            address1IsValid &&
            cityIsValid &&
            stateIsValid &&
            postalCodeIsValid &&
            contactPhoneIsValid;

        return {
            emailIsValid,
            dobIsValid,
            firstNameIsValid,
            lastNameIsValid,
            address1IsValid,
            cityIsValid,
            stateIsValid,
            postalCodeIsValid,
            contactPhoneIsValid,
            isValid
        };
    };

    const handleInputChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const downloadPdfRequisition = (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Donor_${props.donorId}_Requisition.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleSubmit = () => {
        setDirty(true);

        const validation = validate(true);

        if (validation.isValid && !isSubmitting) {
            setIsSubmitting(true);
            props.showLoading('Creating bulk cheek swab order, one moment please...').then(() => {
                props
                    .createDonorOrder(props.donorId, formData)
                    .then((response) => {
                        props.hideLoading();

                        // Check if response is a Blob (PDF)
                        if (response.data instanceof Blob) {
                            downloadPdfRequisition(response.data);
                            toast.success('Bulk cheek swab order created successfully. Requisition downloaded.');
                            props.onSuccess();
                        } else if (response.data && response.data.success) {
                            toast.success('Bulk cheek swab order created successfully');
                            props.onSuccess();
                        } else {
                            toast.error(
                                response.data?.message || 'Unable to create bulk cheek swab order'
                            );
                            setIsSubmitting(false);
                        }
                    })
                    .catch((err: any) => {
                        props.hideLoading();
                        toast.error('Unable to process request');
                        setIsSubmitting(false);
                    });
            });
        }
    };

    const validation = validate(dirty);

    const usStates = [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    return (
        <Modal show={props.show} backdrop="static" onHide={props.onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Utilize a Bulk Cheek Swab (Kit On-hand)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!validation.isValid && dirty && (
                    <Alert bsStyle="danger" style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                        The fields in <strong>red</strong> are required...
                    </Alert>
                )}

                <FormGroup validationState={!validation.emailIsValid ? 'error' : null}>
                    <ControlLabel>Email</ControlLabel>
                    <FormControl
                        name="email"
                        type="email"
                        placeholder="email@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                </FormGroup>

                <FormGroup validationState={!validation.dobIsValid ? 'error' : null}>
                    <ControlLabel>Date of Birth</ControlLabel>
                    <FormControl
                        name="dob"
                        type="date"
                        value={formData.dob}
                        onChange={handleInputChange}
                    />
                </FormGroup>

                <Row>
                    <Col md={6}>
                        <FormGroup validationState={!validation.firstNameIsValid ? 'error' : null}>
                            <ControlLabel>First Name</ControlLabel>
                            <FormControl
                                name="firstName"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup validationState={!validation.lastNameIsValid ? 'error' : null}>
                            <ControlLabel>Last Name</ControlLabel>
                            <FormControl
                                name="lastName"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <FormGroup validationState={!validation.address1IsValid ? 'error' : null}>
                    <ControlLabel>Address 1</ControlLabel>
                    <FormControl
                        name="address1"
                        placeholder="123 Main Street"
                        value={formData.address1}
                        onChange={handleInputChange}
                    />
                </FormGroup>

                <FormGroup>
                    <ControlLabel>Address 2 (Optional)</ControlLabel>
                    <FormControl
                        name="address2"
                        placeholder="Apt 4B"
                        value={formData.address2}
                        onChange={handleInputChange}
                    />
                </FormGroup>

                <Row>
                    <Col md={6}>
                        <FormGroup validationState={!validation.cityIsValid ? 'error' : null}>
                            <ControlLabel>City</ControlLabel>
                            <FormControl
                                name="city"
                                placeholder="City"
                                value={formData.city}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup validationState={!validation.stateIsValid ? 'error' : null}>
                            <ControlLabel>State</ControlLabel>
                            <FormControl
                                name="state"
                                componentClass="select"
                                value={formData.state}
                                onChange={handleInputChange}
                            >
                                <option value="">- Select -</option>
                                {usStates.map((state) => (
                                    <option key={state} value={state}>
                                        {state}
                                    </option>
                                ))}
                            </FormControl>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup validationState={!validation.postalCodeIsValid ? 'error' : null}>
                            <ControlLabel>Zip Code</ControlLabel>
                            <FormControl
                                name="postalCode"
                                placeholder="12345"
                                maxLength={10}
                                value={formData.postalCode}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <FormGroup validationState={!validation.contactPhoneIsValid ? 'error' : null}>
                    <ControlLabel>Phone</ControlLabel>
                    <FormControl
                        name="contactPhone"
                        placeholder="123-456-7890"
                        value={formData.contactPhone}
                        onChange={handleInputChange}
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    Create Order & Download Requisition
                </Button>
                <Button onClick={props.onCancel} disabled={isSubmitting}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        createDonorOrder: (donorId: number, request: ICheekSwabRequest) => {
            return dispatch(OrdersActionCreators.createDonorOrder(donorId, request));
        },
        showLoading: (message: string) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        }
    };
};

export default connect<null, IDispatchProps, IProps>(null, mapDispatchToProps)(BulkCheekSwabModal);
